/**
 * Draft Orders (mode "demande de devis").
 *
 * Convention HTML (compatible Smootify) : poser `data-draft="true"` sur
 * le wrapper `<smootify-cart>` ou similaire pour basculer le panier en
 * mode devis. Au lieu de checkout direct, le submit du form crée un
 * draft order Shopify via le worker (Admin API), envoie l'invoice au
 * client par email, et vide le cart Storefront.
 *
 * Champs requis dans le DOM :
 *   <smootify-cart data-draft="true">
 *     <input name="email" type="email" required>     <!-- obligatoire -->
 *     <input name="firstName" type="text">           <!-- optionnel -->
 *     <input name="lastName" type="text">            <!-- optionnel -->
 *     <input name="phone" type="tel">                <!-- optionnel -->
 *     <textarea name="Note" data-action="cart-note"></textarea>
 *     <button type="submit" data-action="quote-submit">Demander un devis</button>
 *
 *     <div cart-condition="items-in-quote">…</div>     <!-- visible si quote success -->
 *     <div cart-condition="no-items-in-quote">…</div>  <!-- visible sinon -->
 *   </smootify-cart>
 *
 * Endpoint : POST {workerBase}/draft-orders/create
 */

import { config } from "../config.js";
import { getCurrentCart, clearCart } from "../cart/state.js";

const STORAGE_KEY_QUOTE = "lmdr-last-quote";

interface QuoteState {
  id: string;
  name: string;
  invoiceUrl: string | null;
  createdAt: string;
}

function readQuoteState(): QuoteState | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_QUOTE);
    return raw ? (JSON.parse(raw) as QuoteState) : null;
  } catch {
    return null;
  }
}

function writeQuoteState(state: QuoteState | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (state === null) localStorage.removeItem(STORAGE_KEY_QUOTE);
    else localStorage.setItem(STORAGE_KEY_QUOTE, JSON.stringify(state));
  } catch {
    /* noop */
  }
}

function resolveDraftEndpoint(): string | null {
  // Priorité 1 : config explicite
  if (config.draftOrdersUrl) return config.draftOrdersUrl;
  // Priorité 2 : auto-dérive depuis l'URL du bundle (même worker)
  if (typeof document === "undefined") return null;
  const scripts = document.querySelectorAll<HTMLScriptElement>("script[src]");
  for (const s of Array.from(scripts)) {
    if (s.src.includes("/lmdr-commerce/") && s.src.includes("/index.js")) {
      const base = s.src.replace(/\/lmdr-commerce\/[^/]+\/index\.js.*$/, "");
      return `${base}/sync/draft-orders/create`;
    }
  }
  return null;
}

interface SubmitResult {
  ok: boolean;
  data?: QuoteState;
  error?: string;
}

async function submitDraft(wrapper: HTMLElement): Promise<SubmitResult> {
  const cart = getCurrentCart();
  if (!cart || !cart.lines || cart.lines.length === 0) {
    return { ok: false, error: "Panier vide" };
  }

  // Lit customer infos depuis le form
  const emailInput = wrapper.querySelector<HTMLInputElement>(
    'input[name="email"]',
  );
  const email = emailInput?.value?.trim() ?? "";
  if (!email || !email.includes("@")) {
    return { ok: false, error: "Email valide requis" };
  }
  const firstName =
    wrapper.querySelector<HTMLInputElement>('input[name="firstName"]')?.value?.trim() ?? "";
  const lastName =
    wrapper.querySelector<HTMLInputElement>('input[name="lastName"]')?.value?.trim() ?? "";
  const phone =
    wrapper.querySelector<HTMLInputElement>('input[name="phone"]')?.value?.trim() ?? "";
  const note =
    wrapper.querySelector<HTMLTextAreaElement>('textarea[name="Note"]')?.value?.trim() ??
    wrapper.querySelector<HTMLTextAreaElement>('textarea[data-action="cart-note"]')?.value?.trim() ??
    "";

  const lines = cart.lines.map((l) => ({
    variantId: l.merchandise?.id ?? "",
    quantity: l.quantity,
  }));
  if (lines.some((l) => !l.variantId)) {
    return { ok: false, error: "Variantes invalides dans le panier" };
  }

  const url = resolveDraftEndpoint();
  if (!url) return { ok: false, error: "Draft endpoint non configuré" };

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        lines,
        customer: { email, firstName, lastName, phone },
        note,
      }),
    });
    const json = (await res.json()) as
      | { id: string; name: string; invoiceUrl: string | null }
      | { error: string };
    if (!res.ok || "error" in json) {
      return {
        ok: false,
        error: "error" in json ? json.error : `HTTP ${res.status}`,
      };
    }
    const quote: QuoteState = {
      id: json.id,
      name: json.name,
      invoiceUrl: json.invoiceUrl,
      createdAt: new Date().toISOString(),
    };
    writeQuoteState(quote);
    // Vide le cart Storefront (transformé en quote)
    void clearCart();
    return { ok: true, data: quote };
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
}

function refreshQuoteConditions(doc: Document): void {
  const hasQuote = readQuoteState() !== null;
  doc
    .querySelectorAll<HTMLElement>('[cart-condition="items-in-quote"]')
    .forEach((el) => {
      el.classList.toggle("lmdr-hidden", !hasQuote);
    });
  doc
    .querySelectorAll<HTMLElement>('[cart-condition="no-items-in-quote"]')
    .forEach((el) => {
      el.classList.toggle("lmdr-hidden", hasQuote);
    });
  // Hydrate les éléments quote-* (display name + invoice link)
  const quote = readQuoteState();
  if (quote) {
    doc.querySelectorAll<HTMLElement>('[quote="name"]').forEach((el) => {
      el.textContent = quote.name;
    });
    doc.querySelectorAll<HTMLAnchorElement>('a[quote="invoice-url"]').forEach((a) => {
      if (quote.invoiceUrl) a.href = quote.invoiceUrl;
    });
  }
}

function setStateText(wrapper: HTMLElement, kind: "loading" | "success" | "error", message: string): void {
  wrapper.querySelectorAll<HTMLElement>('[data-quote-feedback]').forEach((el) => {
    el.textContent = message;
    el.classList.remove("lmdr-quote-loading", "lmdr-quote-success", "lmdr-quote-error");
    el.classList.add(`lmdr-quote-${kind}`);
  });
}

export function attachDraftCart(doc: Document = document): void {
  if (typeof document === "undefined") return;
  if ((document as unknown as { __lmdrDraftBound?: boolean }).__lmdrDraftBound) return;
  (document as unknown as { __lmdrDraftBound?: boolean }).__lmdrDraftBound = true;

  // Capture submit sur les forms à l'intérieur d'un <smootify-cart data-draft="true">
  doc.addEventListener(
    "submit",
    (ev) => {
      const target = ev.target as HTMLElement | null;
      if (!target || target.tagName !== "FORM") return;
      const wrapper = target.closest<HTMLElement>(
        'smootify-cart[data-draft="true"], [data-draft="true"]',
      );
      if (!wrapper) return;

      ev.preventDefault();
      ev.stopPropagation();

      const submitBtn = target.querySelector<HTMLButtonElement>(
        'button[type="submit"], button[data-action="quote-submit"]',
      );
      if (submitBtn) submitBtn.disabled = true;
      setStateText(wrapper, "loading", "Création du devis...");

      void submitDraft(wrapper).then((result) => {
        if (submitBtn) submitBtn.disabled = false;
        if (result.ok && result.data) {
          setStateText(wrapper, "success", `Devis ${result.data.name} créé`);
          refreshQuoteConditions(doc);
        } else {
          setStateText(wrapper, "error", result.error ?? "Erreur création devis");
        }
      });
    },
    true,
  );

  // Hydrate au boot + à chaque cart_updated (vide cart → toggle conditions)
  refreshQuoteConditions(doc);
  document.addEventListener("smootify:cart_updated", () => refreshQuoteConditions(doc));
}
